/**
 * Phase 8 Round A bootstrap.
 *
 * Layout: a fixed left sidebar (Sidebar component) + a fluid right main
 * pane. The hash router drives which view gets rendered into the main
 * pane. The global language toggle lives at the bottom of the sidebar
 * and persists via `localStorage`.
 *
 * The `<body>` receives `lang-zh` / `lang-en` so any component can use
 * CSS to hide the inactive language side without subscribing to the
 * store (the cost is O(1) on toggle).
 */

import "./styles.css";
import { setChildren, el } from "./dom";
import { sidebar } from "./components/sidebar";
import { startRouter, parseHash } from "./router";
import { on as onStore, setRoute, getStore } from "./store";
import type { Route } from "./store";
import { getLang, subscribeLang } from "./i18n";
import { renderDailyView, renderDailyViewForDate } from "./views/daily";
import { renderWeekView } from "./views/week";
import { renderWeeksIndexView } from "./views/weeks";
import { renderArticleView } from "./views/article";
import { renderChangelogView } from "./views/changelog";
import { renderMoreView } from "./views/more";
import { renderNotFoundView } from "./views/notFound";

function mount(): void {
  const appRoot = document.getElementById("app");
  if (!appRoot) return;

  const mainPane = el("main", { class: "main-content" });
  const shell = el("div", { class: "app-shell" }, [
    sidebar(),
    el("section", { class: "app-main" }, [mainPane]),
  ]);
  setChildren(appRoot, [shell]);

  syncBodyLang(getLang());
  subscribeLang(syncBodyLang);

  startRouter();

  // Initial render based on the (already-set) route.
  renderInto(mainPane);

  // Re-render whenever the route changes — the store emits `change`
  // for every state mutation (cache, lang, route), so we compare the
  // previous + current route names to avoid spurious re-renders.
  let prevRouteName = getStore().route.name;
  onStore((next, prev) => {
    if (next.route.name !== prev.route.name) {
      prevRouteName = next.route.name;
      renderInto(mainPane);
    }
    void prevRouteName;
  });
}

function syncBodyLang(lang: "zh" | "en"): void {
  document.body.classList.toggle("lang-zh", lang === "zh");
  document.body.classList.toggle("lang-en", lang === "en");
  // Sanity guard — the initial render happens before any user toggle,
  // but if a future test fires subscribeLang before mount we still
  // want the body to reflect the active language.
  if (document.body.classList.length === 0) {
    document.body.classList.add(lang === "zh" ? "lang-zh" : "lang-en");
  }
}

function renderInto(host: HTMLElement): void {
  const route = getStore().route;
  let node: HTMLElement;

  switch (route.name) {
    case "daily":
      node = renderDailyView();
      break;
    case "dailyByDate": {
      const date = route.params["date"];
      node = renderDailyViewForDate(date ?? "");
      break;
    }
    case "week": {
      const id = route.params["id"];
      node = renderWeekView(id ?? "");
      break;
    }
    case "weeks":
      node = renderWeeksIndexView();
      break;
    case "article": {
      const pmid = route.params["pmid"];
      node = renderArticleView(pmid ?? "");
      break;
    }
    case "changelog":
      node = renderChangelogView();
      break;
    case "more": {
      // `#/more/changelog` puts `child=changelog` in route.params;
      // `#/more` (no child key) defaults to the weeks index.
      const child = route.params["child"] === "changelog" ? "changelog" : "weeks";
      node = renderMoreView({ child });
      break;
    }
    case "notFound":
    default:
      node = renderNotFoundView();
      break;
  }

  setChildren(host, [node]);
  // Ensure the page starts from the top on every route change —
  // improves UX when switching tabs.
  window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
}

// Re-entrant guard — `parseHash` import keeps the store type narrow.
void parseHash;
void setRoute;

// Augment window for debugging.
declare global {
  interface Window {
    __parseHash?: typeof parseHash;
    __route?: Route;
  }
}

if (typeof window !== "undefined") {
  window.__parseHash = parseHash;
}

mount();
